/* eslint-disable react/no-unknown-property */
import React from 'react';
import {
  Action,
  AutoCompleteV2,
  AutoCompleteV2OnSuggestionSelected,
  FontWeight,
  Badge,
  Grid,
  HStack,
  Icon,
  ICON_ARROW_UP,
  ICON_INFORMATION_OUTLINE,
  ICON_SWAP_HORIZONTAL,
  Image,
  InputAction,
  Message,
  ModalSize,
  Portal,
  Select,
  Stack,
  Text,
  useModal,
  useNotifications,
} from '@doveit/bricks';
import { formatDate } from '@doveit/hammer';
import SimpleModal from '../../../components/simple-modal/SimpleModal';
import MultimediaContentThumbnail from '../../../content/components/MultimediaContentThumbnail';
import { usePropertyAvailableAssets } from '../../hooks/use-property-available-assets/usePropertyAvailableAssets';
import useAllShootingsByPropertyId from '../../hooks/use-all-shootings-by-property-id/useAllShootingsByPropertyId';
import {
  ContentAbout, ContentAboutType, ContentType, ShootingStatus,
} from '../../../domain/types';
import { contentAboutLabels } from '../../../labels';
import { SortOrder } from '../../../providers/pagination';
import { updateContentsSelection } from '../../../providers/api/content/contentProvider';
import { Content } from '../../../providers/api/dtos';
import useIsDevice from '../../../hooks/use-is-device/useIsDevice';
import * as styles from './ReplacePhotoByShootingAction.style';

const ALL_SHOOTINGS = 'all';

interface ContentAboutSuggestion {
  value: ContentAboutType,
  label: string,
}

const allContentAboutSuggestions: ContentAboutSuggestion[] = Object.values(ContentAbout).map((contentAbout) => ({
  value: contentAbout as ContentAboutType,
  label: contentAboutLabels[contentAbout as ContentAboutType],
}));

const toContentAboutSuggestion = (about?: string): ContentAboutSuggestion | undefined => (about ? {
  value: about as ContentAboutType,
  label: contentAboutLabels[about as ContentAboutType],
} : undefined);

const printContentAboutSuggestion = (suggestion: ContentAboutSuggestion): string => suggestion.label;

/** Same behaviour as the description autocomplete of the property gallery: search, select or clear */
const ContentAboutAutocomplete: React.FC<{
  value?: string,
  onChange: (about?: string) => void,
  disabled?: boolean,
}> = ({ value, onChange, disabled }) => {
  const selectedSuggestion = React.useMemo(() => toContentAboutSuggestion(value), [value]);
  const [descriptionInput, setDescriptionInput] = React.useState<string>(selectedSuggestion?.label ?? '');

  React.useEffect(() => {
    setDescriptionInput(selectedSuggestion?.label ?? '');
  }, [selectedSuggestion]);

  const suggestions = React.useMemo(() => {
    if (!descriptionInput) {
      return allContentAboutSuggestions;
    }

    return allContentAboutSuggestions.filter((suggestion) => suggestion.label.toLocaleLowerCase().includes(descriptionInput.toLocaleLowerCase()));
  }, [descriptionInput]);

  const onSuggestionSelected: AutoCompleteV2OnSuggestionSelected<ContentAboutSuggestion | null> = React.useCallback((suggestion) => {
    setDescriptionInput(suggestion?.label ?? '');
    onChange(suggestion?.value);
  }, [onChange]);

  const onInputChange = React.useCallback((searchText: string, action: InputAction) => {
    switch (action) {
      case 'input-change':
        setDescriptionInput(searchText);
        break;
      case 'input-blur':
        setDescriptionInput(selectedSuggestion?.label ?? '');
        break;
      default:
    }
  }, [selectedSuggestion]);

  return (
    <AutoCompleteV2
      aria-label="Descrizione della nuova foto"
      suggestions={suggestions}
      printSuggestion={printContentAboutSuggestion}
      onSuggestionSelected={onSuggestionSelected}
      minLength={1}
      value={selectedSuggestion}
      placeholder="Descrizione"
      clearValueOnSelection
      onInputChange={onInputChange}
      inputValue={descriptionInput}
      disabled={disabled}
    />
  );
};

interface ChildFnProps {
  replacePhoto: VoidFunction;
  disabled: boolean;
  loading: boolean;
}

export interface ReplacePhotoByShootingActionProps {
  propertyId: number;
  /** The gallery photo to be replaced */
  photo: Content;
  /** 0-based position of the photo in the gallery, used in labels */
  photoIndex?: number;
  disabled?: boolean;
  children?: (props: ChildFnProps) => React.ReactNode;
  onSuccess?: (payload: Content[]) => void;
}

const ReplacePhotoByShootingAction: React.FC<ReplacePhotoByShootingActionProps> = ({
  propertyId,
  photo: galleryPhoto,
  photoIndex: galleryPhotoIndex,
  disabled = false,
  children,
  onSuccess,
}) => {
  const {
    data: availableAssets, error: availableAssetsError, isLoading, mutate,
  } = usePropertyAvailableAssets(propertyId, { contentType: [ContentType.FOTO] });
  const { data: shootings } = useAllShootingsByPropertyId(propertyId, { status: [ShootingStatus.COMPLETED, ShootingStatus.CLOSED] }, { page: 0, size: 20, sort: { dueDate: SortOrder.DESC } });

  const isMobile = useIsDevice('mobile');
  const replacePhotoModal = useModal();
  const comparisonRef = React.useRef<HTMLDivElement>(null);
  const filmStripRef = React.useRef<HTMLDivElement>(null);
  const [showTopFade, setShowTopFade] = React.useState(false);
  const [showBottomFade, setShowBottomFade] = React.useState(false);
  const { addSuccess, addError } = useNotifications();

  const [selectedShootingId, setSelectedShootingId] = React.useState<string>(ALL_SHOOTINGS);
  const [newPhotoName, setNewPhotoName] = React.useState<string | undefined>();
  const [newPhotoAbout, setNewPhotoAbout] = React.useState<string | undefined>();
  const [isSaving, setIsSaving] = React.useState(false);
  // The photo to replace is fixed when the modal opens: on success the gallery is updated (and so the props)
  // while the modal is still open, and the current photo must not become the new one
  const [{ photo, photoIndex }, setPhotoToReplace] = React.useState({ photo: galleryPhoto, photoIndex: galleryPhotoIndex });

  const photos = React.useMemo(
    () => (availableAssets ?? []).filter((asset) => !!asset.shootingId),
    [availableAssets],
  );

  const newPhoto = React.useMemo(
    () => photos.find((p) => p.fileName === newPhotoName),
    [photos, newPhotoName],
  );

  // The only numbering shown to the user is the gallery one (1-based) of the photo to replace
  const photoPosition = photoIndex !== undefined ? photoIndex + 1 : undefined;
  const photoLabel = photoPosition !== undefined ? `Foto ${photoPosition}` : 'Foto';

  const shootingOptions = React.useMemo(() => {
    const options = [{ label: 'Tutti gli shooting', value: ALL_SHOOTINGS }];

    shootings?.forEach((shooting) => {
      if (shooting.id && photos.some((p) => p.shootingId === shooting.id)) {
        options.push({
          label: `${shooting.photographerName} - ${formatDate(new Date(shooting.dueDate))}`,
          value: shooting.id,
        });
      }
    });

    return options;
  }, [shootings, photos]);

  const shownPhotos = React.useMemo(
    () => photos.filter((p) => selectedShootingId === ALL_SHOOTINGS || p.shootingId === selectedShootingId),
    [photos, selectedShootingId],
  );

  const replacePhoto = React.useCallback(() => {
    setSelectedShootingId(ALL_SHOOTINGS);
    setNewPhotoName(undefined);
    setNewPhotoAbout(undefined);
    setPhotoToReplace({ photo: galleryPhoto, photoIndex: galleryPhotoIndex });
    replacePhotoModal.open();
  }, [replacePhotoModal, galleryPhoto, galleryPhotoIndex]);

  const handleFilmStripScroll = React.useCallback(() => {
    if (!filmStripRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = filmStripRef.current;
    setShowTopFade(scrollTop > 0);
    setShowBottomFade(Math.ceil(scrollTop + clientHeight) < scrollHeight - 1);
  }, []);

  React.useEffect(() => {
    const timer = setTimeout(handleFilmStripScroll, 50);
    return () => clearTimeout(timer);
  }, [handleFilmStripScroll, shownPhotos, replacePhotoModal.isOpen]);

  const handleThumbnailClick = React.useCallback((fileName: string) => {
    const isDeselection = fileName === newPhotoName;

    setNewPhotoName(isDeselection ? undefined : fileName);
    // The description starts from the one of the chosen photo (if any)
    setNewPhotoAbout(isDeselection ? undefined : photos.find((p) => p.fileName === fileName)?.about);

    // On mobile the comparison is above the photo list: bring it back into view after a choice
    if (isMobile && !isDeselection) {
      comparisonRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    }
  }, [isMobile, newPhotoName, photos]);

  const handleSave = async () => {
    if (!newPhoto) return;

    try {
      setIsSaving(true);

      const payload: Content[] = [
        { ...photo, selected: false },
        {
          ...newPhoto,
          selected: true,
          position: photo.position,
          about: newPhotoAbout,
        },
      ];

      await updateContentsSelection(payload);
      onSuccess?.(payload);
      await mutate();
      replacePhotoModal.close();
      addSuccess('Foto sostituita con successo.');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      addError('Errore durante la sostituzione della foto.');
    } finally {
      setIsSaving(false);
    }
  };

  // While checking the available assets the action is loading (not disabled):
  // it gets disabled only when the check fails or finds no shooting photos
  const isDisabled = disabled || (!isLoading && (!!availableAssetsError || photos.length === 0));
  const currentPhotoCaption = [
    photoPosition !== undefined ? `${photoLabel} in galleria` : photoLabel,
    photo.about && contentAboutLabels[photo.about as ContentAboutType],
    // On mobile there is no file name badge on the (small) current photo
    isMobile && photo.fileName,
  ].filter(Boolean).join(' · ');

  const newPhotoCaption = photoPosition !== undefined
    ? `Prenderà la posizione ${photoPosition} in galleria`
    : 'Prenderà il posto della foto attuale';

  const withGalleryPosition = (label: string, position?: number) => (position !== undefined ? `${label} · ${position}` : label);

  const shootingFilter = (
    <Select
      aria-label="Filtra per shooting"
      value={selectedShootingId}
      options={shootingOptions}
      size="S"
      onChange={(e) => setSelectedShootingId(e.target.value as string)}
      disabled={isSaving}
      width="100%"
    />
  );

  const thumbnails = shownPhotos.length === 0 ? (
    <Message type="info" message="Nessuna foto presente per questo filtro." />
  ) : (
    <Grid gutter={25}>
      {shownPhotos.map((content) => {
        const isCurrent = content.fileName === photo.fileName;
        const isNew = content.fileName === newPhotoName;
        const isInGallery = content.selected && !isCurrent;
        const isSelectable = !isCurrent && !isInGallery && !isSaving;

        let thumbnailBadgeLabel: string | undefined;
        if (isCurrent) thumbnailBadgeLabel = withGalleryPosition('Attuale', photoPosition);
        else if (isNew) thumbnailBadgeLabel = 'Nuova';
        else if (isInGallery) thumbnailBadgeLabel = 'In galleria';

        return (
          <Grid.Unit size={isMobile ? 1 / 3 : 1 / 2} key={content.fileName}>
            <MultimediaContentThumbnail
              aria-label={`Foto dello shooting ${content.fileName}`}
              picture={{ src: content.thumbnail ?? content.url ?? '', alt: content.fileName }}
              aspectRatio="3 / 2"
              active={isNew}
              selected={isNew || isCurrent}
              disabled={isInGallery}
              badgeLabel={thumbnailBadgeLabel}
              onClick={isSelectable ? () => handleThumbnailClick(content.fileName) : undefined}
            />
          </Grid.Unit>
        );
      })}
    </Grid>
  );

  const currentPhotoImage = (
    <div css={styles.imageWrapper}>
      {(photo.thumbnail || photo.url) && (
        <Image
          src={photo.thumbnail ?? photo.url!}
          alt={`${photoLabel} attuale`}
          responsive
        />
      )}
      {/* On mobile the current photo is a small preview: there is no room for the file name */}
      {!isMobile && (
        <div css={styles.fileName} title={photo.fileName}>
          <Badge size="XXS" label={photo.fileName} />
        </div>
      )}
    </div>
  );

  const aboutSelect = (
    <div css={styles.aboutAutocomplete} $isMobile={isMobile}>
      <ContentAboutAutocomplete
        key={newPhotoName}
        value={newPhotoAbout}
        onChange={setNewPhotoAbout}
        disabled={isSaving}
      />
    </div>
  );

  const newPhotoImage = newPhoto && (
    <div css={styles.imageWrapper} $highlighted>
      <Image
        src={newPhoto.url ?? newPhoto.thumbnail!}
        alt="Nuova foto"
        responsive
      />
      <div css={styles.fileName} title={newPhoto.fileName}>
        <Badge size="XS" label={newPhoto.fileName} />
      </div>
    </div>
  );

  const emptyNewPhotoMessage = (
    <Message
      type="neutral"
      message={isMobile
        ? 'Seleziona una foto dall\'elenco qui sotto per sostituire quella attuale.'
        : 'Seleziona una foto dall\'elenco per confrontarla con quella attuale.'}
    />
  );

  const desktopContent = (
    <div css={styles.base}>
      <div css={styles.sideContainer}>
        <div css={styles.side}>
          <div css={styles.filters}>
            {shootingFilter}
          </div>
          <div css={styles.filmStripWrapper}>
            <div css={styles.filmStripFadeTop} style={{ opacity: showTopFade ? 1 : 0 }} />
            <div
              css={styles.filmStrip}
              ref={filmStripRef}
              onScroll={handleFilmStripScroll}
              aria-label="Elenco foto dello shooting"
            >
              {thumbnails}
            </div>
            <div css={styles.filmStripFadeBottom} style={{ opacity: showBottomFade ? 1 : 0 }} />
          </div>
        </div>
      </div>
      <div css={styles.comparison}>
        <div css={styles.currentPhoto}>
          <Stack gap={100}>
            <Text.Caption fontWeight={FontWeight.MEDIUM}>Attuale</Text.Caption>
            {currentPhotoImage}
            <Text.Caption>{currentPhotoCaption}</Text.Caption>
          </Stack>
        </div>
        <div css={styles.arrow}>
          <Icon path={ICON_ARROW_UP} direction="right" size={20} />
        </div>
        <div css={styles.newPhoto}>
          {newPhoto ? (
            <Stack gap={100}>
              <HStack gap={150} alignItems="center" justifyContent="space-between">
                <Stack gap={0}>
                  <Text.Caption color="primary.default.high" fontWeight={FontWeight.MEDIUM}>Nuova</Text.Caption>
                  <Text.Caption>{newPhotoCaption}</Text.Caption>
                </Stack>
                {aboutSelect}
              </HStack>
              {newPhotoImage}
            </Stack>
          ) : emptyNewPhotoMessage}
        </div>
      </div>
    </div>
  );

  const mobileContent = (
    <div css={styles.mobileBase} aria-label="Sostituzione foto (mobile)">
      <div css={styles.mobileComparison} ref={comparisonRef}>
        <div css={styles.mobileCurrentPhoto}>
          <div css={styles.mobileCurrentPhotoImage}>
            {currentPhotoImage}
          </div>
          <Stack gap={0}>
            <Text.Caption fontWeight={FontWeight.MEDIUM}>Attuale</Text.Caption>
            <Text.BodySmall>{currentPhotoCaption}</Text.BodySmall>
          </Stack>
        </div>
        <div css={styles.mobileArrow}>
          <div css={styles.arrow}>
            <Icon path={ICON_ARROW_UP} direction="bottom" size={20} />
          </div>
        </div>
        {newPhoto ? (
          <Stack gap={100}>
            <Stack gap={0}>
              <Text.Caption color="primary.default.high" fontWeight={FontWeight.MEDIUM}>Nuova</Text.Caption>
              <Text.Caption>{newPhotoCaption}</Text.Caption>
            </Stack>
            {newPhotoImage}
            {aboutSelect}
          </Stack>
        ) : emptyNewPhotoMessage}
      </div>
      <Stack gap={100}>
        {shootingFilter}
      </Stack>
      {thumbnails}
    </div>
  );

  const note = (
    <HStack gap={100} alignItems="center" wrap="nowrap">
      <Icon path={ICON_INFORMATION_OUTLINE} size={16} />
      <Text.BodySmall>
        {isMobile
          ? 'La foto attuale resta disponibile nello shooting.'
          : 'La foto attuale viene sostituita nella galleria ma resta disponibile nello shooting.'}
      </Text.BodySmall>
    </HStack>
  );

  const actions = (
    <div css={styles.footerActions} $isMobile={isMobile}>
      <Action
        color="neutral"
        label="Annulla"
        onClick={replacePhotoModal.close}
        disabled={isSaving}
        expanded={isMobile}
      />
      <Action
        color="primary"
        emphasis="high"
        label={isMobile ? 'Sostituisci' : 'Sostituisci foto'}
        iconLeft={{ path: ICON_SWAP_HORIZONTAL }}
        onClick={handleSave}
        loading={isSaving}
        disabled={!newPhoto}
        expanded={isMobile}
        aria-label="Conferma sostituzione foto"
      />
    </div>
  );

  return (
    <>
      {children
        ? children({ replacePhoto, disabled: isDisabled, loading: isLoading })
        : (
          <Action
            label="Sostituisci"
            onClick={replacePhoto}
            disabled={isDisabled}
            loading={isLoading}
            iconLeft={{ path: ICON_SWAP_HORIZONTAL }}
            size="S"
            aria-label="Pulsante per sostituire la foto con una dello shooting"
          />
        )}
      <Portal>
        <SimpleModal
          {...replacePhotoModal}
          close={isSaving ? undefined : replacePhotoModal.close}
          title={photoPosition !== undefined ? `Sostituisci foto ${photoPosition} in galleria` : 'Sostituisci foto'}
          header={(
            <Text.BodySmall>
              {isMobile
                ? 'Scegli una foto: prenderà il posto di quella attuale.'
                : 'Scegli una foto dagli shooting: prenderà il posto della foto attuale.'}
            </Text.BodySmall>
          )}
          size={ModalSize.XLARGE}
          aria-label="Modale per sostituire una foto con una dello shooting"
          footer={(
            <div css={styles.footer} $isMobile={isMobile}>
              {note}
              {actions}
            </div>
          )}
        >
          {isMobile ? mobileContent : desktopContent}
        </SimpleModal>
      </Portal>
    </>
  );
};

export default ReplacePhotoByShootingAction;
